/**
 * BudgetFormSection Component
 *
 * Section 1 of the Gestão de Recursos Edit Page: "Orçamento do Projeto"
 * Displays 9 budget fields (7 editable + 3 auto-calculated)
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - Labels: #F6F6F6 (14px)
 * - Calculated fields: Success green #13D480
 * - Uses existing CurrencyInput component for all money fields
 *
 * Auto-calculations:
 * - Subtotal Financiável = sum of first 5 fields
 * - Total Financiáveis = Subtotal + Taxa de Gerenciamento
 * - Orçamento Total = Total Financiáveis + Taxa de Agenciamento
 */

import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { Controller } from "react-hook-form";
import CurrencyInput from "@/components/ui/currency-input";
import { BudgetSummary } from "@/types/gestao-recursos.interface";
import { GestaoRecursosEditForm } from "@/pages/financeiro/gestao-recursos-editar";

interface BudgetFormSectionProps {
  form: UseFormReturn<GestaoRecursosEditForm>;
}

export function BudgetFormSection({ form }: BudgetFormSectionProps) {
  // Watch all editable fields to trigger auto-calculation
  const [
    desenvolvimento,
    preProducao,
    producao,
    posProducao,
    despesasAdministrativas,
    taxaGerenciamento,
    taxaAgenciamento,
  ] = form.watch([
    "budget.desenvolvimento",
    "budget.preProducao",
    "budget.producao",
    "budget.posProducao",
    "budget.despesasAdministrativas",
    "budget.taxaGerenciamento",
    "budget.taxaAgenciamento",
  ]);

  // Calculate totals automatically
  const subtotalFinanciavel =
    (desenvolvimento || 0) +
    (preProducao || 0) +
    (producao || 0) +
    (posProducao || 0) +
    (despesasAdministrativas || 0);

  const totalFinanciaveis = subtotalFinanciavel + (taxaGerenciamento || 0);
  const orcamentoTotal = totalFinanciaveis + (taxaAgenciamento || 0);

  // Update calculated fields in form whenever inputs change
  useEffect(() => {
    form.setValue("budget.subtotalFinanciavel", subtotalFinanciavel);
    form.setValue("budget.totalFinanciaveis", totalFinanciaveis);
    form.setValue("budget.orcamentoTotal", orcamentoTotal);
  }, [subtotalFinanciavel, totalFinanciaveis, orcamentoTotal, form]);

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[40px]">
      {/* Section Title */}
      <h3 className="text-[20px] font-bold text-[#F6F6F6] mb-[30px]">
        Orçamento do Projeto
      </h3>

      {/* Form Grid - 2 columns */}
      <div className="grid grid-cols-2 gap-x-[30px] gap-y-[24px]">
        {/* Desenvolvimento */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">Desenvolvimento</label>
          <Controller
            name="budget.desenvolvimento"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput field={field} placeholder="R$ 0,00" />
            )}
          />
        </div>

        {/* Pré-Produção */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">Pré-Produção</label>
          <Controller
            name="budget.preProducao"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput field={field} placeholder="R$ 0,00" />
            )}
          />
        </div>

        {/* Produção */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">Produção</label>
          <Controller
            name="budget.producao"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput field={field} placeholder="R$ 0,00" />
            )}
          />
        </div>

        {/* Pós-Produção */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">Pós-Produção</label>
          <Controller
            name="budget.posProducao"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput field={field} placeholder="R$ 0,00" />
            )}
          />
        </div>

        {/* Despesas Administrativas */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Despesas Administrativas
          </label>
          <Controller
            name="budget.despesasAdministrativas"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput field={field} placeholder="R$ 0,00" />
            )}
          />
        </div>

        {/* Subtotal Financiável (CALCULATED - Read-only) */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#13D480] font-semibold">
            Subtotal Financiável
          </label>
          <Controller
            name="budget.subtotalFinanciavel"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput
                field={field}
                placeholder="R$ 0,00"
                disabled={true}
                className="text-[#13D480] font-bold"
              />
            )}
          />
        </div>

        {/* Taxa de Gerenciamento */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Taxa de Gerenciamento (até 10%)
          </label>
          <Controller
            name="budget.taxaGerenciamento"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput field={field} placeholder="R$ 0,00" />
            )}
          />
        </div>

        {/* Taxa de Agenciamento e Colocação */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Taxa de Agenciamento e Colocação (não financiável)
          </label>
          <Controller
            name="budget.taxaAgenciamento"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput field={field} placeholder="R$ 0,00" />
            )}
          />
        </div>

        {/* Total de Itens Financiáveis (CALCULATED - Read-only) */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#13D480] font-semibold">
            Total de Itens Financiáveis
          </label>
          <Controller
            name="budget.totalFinanciaveis"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput
                field={field}
                placeholder="R$ 0,00"
                disabled={true}
                className="text-[#13D480] font-bold"
              />
            )}
          />
        </div>

        {/* Orçamento Total do Projeto (CALCULATED - Read-only) - Full width */}
        <div className="col-span-2 flex flex-col gap-2">
          <label className="text-[14px] text-[#13D480] font-semibold">
            Orçamento Total do Projeto
          </label>
          <Controller
            name="budget.orcamentoTotal"
            control={form.control}
            render={({ field }) => (
              <CurrencyInput
                field={field}
                placeholder="R$ 0,00"
                disabled={true}
                className="text-[#13D480] font-bold"
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
